"use client"

import Link from "next/link"
import { IconCalendarWeek, IconDental } from "@tabler/icons-react"
import { useTranslations } from "next-intl"

import { ApolloPanel } from "@/components/ui/apollo-panel"
import { Button } from "@/components/ui/button"

interface DoctorQuickLinksProps {
  /** Holds `appointments.view` — gates the /calendar shortcut. */
  canViewCalendar: boolean
  /** Holds `kartele.view` — gates the /kartele shortcut. */
  canViewKartele: boolean
  className?: string
}

/**
 * Two-shortcut card next to the lab rail: calendar + patient charts.
 *
 * Hide-not-disable — each button is dropped when its destination page would
 * 403 (both target pages sit behind a PermissionGuard), and the card renders
 * nothing at all when neither grant is held.
 */
export function DoctorQuickLinks({
  canViewCalendar,
  canViewKartele,
  className,
}: DoctorQuickLinksProps) {
  const t = useTranslations("dashboard")

  if (!canViewCalendar && !canViewKartele) return null

  return (
    <ApolloPanel eyebrow={t("quickLinks.title")} className={className}>
      <div className="flex flex-col gap-2">
        {canViewCalendar && (
          <Button asChild variant="outline" className="justify-start">
            <Link href="/calendar">
              <IconCalendarWeek className="h-4 w-4" />
              {t("quickLinks.calendar")}
            </Link>
          </Button>
        )}
        {canViewKartele && (
          <Button asChild variant="outline" className="justify-start">
            <Link href="/kartele">
              <IconDental className="h-4 w-4" />
              {t("quickLinks.kartele")}
            </Link>
          </Button>
        )}
      </div>
    </ApolloPanel>
  )
}
